import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Spin,
  Row,
  Col,
  Button
} from "antd";
import { getRequest } from "../api/api";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;

const McqsFormModal = ({ visible, onCancel, onSubmit, initialData }) => {
  const [form] = Form.useForm();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch chapters once
  useEffect(() => {
    const fetchChapters = async () => {
      try {
        setLoading(true);
        const res = await getRequest("/chapters");
        setChapters(res);
      } catch {
        message.error("Failed to load chapters.");
      } finally {
        setLoading(false);
      }
    };
    fetchChapters();
  }, []);

  // Reset/populate whenever the modal opens
  useEffect(() => {
    if (!visible) return;

    if (initialData) {
      const { option_a, option_b, option_c, option_d, correct_answer } =
        initialData;

      let correctKey = null;
      if (correct_answer === option_a) correctKey = "A";
      else if (correct_answer === option_b) correctKey = "B";
      else if (correct_answer === option_c) correctKey = "C";
      else if (correct_answer === option_d) correctKey = "D";

      form.setFieldsValue({
        ...initialData,
        correct_answer: correctKey,
        // normalize booleans and defaults while editing
        is_active:
          initialData.is_active === 1 ||
          initialData.is_active === "1" ||
          initialData.is_active === true,
        score: initialData.score ?? 1,
      });
    } else {
      // ADD mode → clear and apply defaults
      form.resetFields(); // resets to initialValues below
      // make sure defaults are present even after prior edits
      form.setFieldsValue({ score: 1, is_active: true });
    }
  }, [visible, initialData, form]);

  // Submit handler
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const optionsMap = {
        A: values.option_a,
        B: values.option_b,
        C: values.option_c,
        D: values.option_d,
      };

      const payload = {
        ...values,
        correct_answer: optionsMap[values.correct_answer],
            is_active: initialData ? (values.is_active ? 1 : 0) : 1,
        score: values.score ?? 1,
      };

      onSubmit(payload);
    } catch (err) {
      // validation errors are already shown by antd
      console.log("Validation failed:", err);
    }
  };

  return (
    <Modal
      title={initialData ? "Update MCQ" : "Add New MCQ"}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      okText={initialData ? "Update MCQ" : "Add MCQ"}
      width={700}
      centered
      maskClosable={false}
       footer={[

    <Button
      key="ok"
      type="primary"
      onClick={handleOk}
      icon={initialData ? <EditOutlined /> : <PlusOutlined />}
    >
      {initialData ? "Update MCQ" : "Add MCQ"}
    </Button>,
  ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          // defaults used when adding and after resetFields()
          initialValues={{ score: 1, is_active: true }}
        >
          <Form.Item
            name="chapter_id"
            label="Chapter"
            rules={[{ required: true }]}
            className="mcq-form-input"
          >
            <Select
              showSearch
              placeholder="Search and select chapter"
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {chapters.map((chapter) => (
                <Option key={chapter.id} value={chapter.id}>
                  {chapter.title}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true }]}
            className="mcq-form-input"
          >
            <TextArea
              rows={3}
              placeholder="Which three bones form the shoulder complex?"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="option_a"
                label="Option A"
                rules={[{ required: true }]}
                className="mcq-form-input"
              >
                <Input placeholder="Enter option A" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="option_b"
                label="Option B"
                rules={[{ required: true }]}
                className="mcq-form-input"
              >
                <Input placeholder="Enter option B" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="option_c"
                label="Option C"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter option C" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="option_d"
                label="Option D"
                rules={[{ required: true }]}
              >
                <Input placeholder="Enter option D" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="correct_answer"
            label="Correct Answer"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select correct answer">
              <Option value="A">Option A</Option>
              <Option value="B">Option B</Option>
              <Option value="C">Option C</Option>
              <Option value="D">Option D</Option>
            </Select>
          </Form.Item>

          <Form.Item name="explanation" label="Explanation">
            <TextArea rows={2} placeholder="Add explanation" />
          </Form.Item>

          {/* <Row gutter={16}> */}
          <Col span={12}>
            <Form.Item name="score" label="Correct Answer Score">
              <InputNumber min={1} max={10} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          
         {/* ...above code... */}



{/* Active toggle — only in EDIT mode */}
{/* {initialData && (
  <Col span={12}>
    <Form.Item
      name="is_active"
      label="MCQ Active"
      valuePropName="checked"
      style={{ marginTop: 29 }}
    >
      <Switch />
    </Form.Item>
  </Col>
)} */}

{initialData && (
  <Form.Item noStyle shouldUpdate>
    {({ getFieldValue }) => {
      const on = !!getFieldValue("is_active");
      return (
        <Form.Item
          name="is_active"
          label="Status"
          valuePropName="checked"
          className="custom-form-label"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            className={`status-switch ${on ? "on" : "off"}`}
          />
        </Form.Item>
      );
    }}
  </Form.Item>
)}


{/* ...below code... */}

          {/* </Row> */}
        </Form>
      </Spin>
    </Modal>
  );
};

export default McqsFormModal;
